// Earth tile → sim-input conversion (ERT-2/4). PURE (ENG-1). Turns decoded TIL
// tiles (raw WGS84 vector features + base64 DEM grids) into the SAME serializable
// shapes the Atlas pipeline already voxelizes: an `IntermediateCity` (block coords)
// and a `DemTile` (lat/lon raster). The projection is `projectLatLon` (the sim owns
// it, TIL-2 — identical to the Atlas ENU projection), so Earth stamps geometry
// through the existing sim/atlas modules unchanged (ERT-4).
import { projectLatLon, decodeDemData, type LatLon, type DemTilePayload, type VectorTilePayload } from './tiles';
import type { IntermediateCity } from '../atlas/intermediate';
import { NO_DATA, type DemTile } from '../atlas/elevation';

export { earthRange, earthRangeClamped } from './range';

/** Project a raw [lat,lon] point to a block [x,z] around `center` at `scaleM`. */
function project(center: LatLon, scaleM: number, ll: readonly [number, number]): [number, number] {
  const [lat, lon] = ll;
  return projectLatLon(lat, lon, center, scaleM);
}
const projectRing = (center: LatLon, scaleM: number, ring: readonly (readonly [number, number])[]): [number, number][] =>
  ring.map((p) => project(center, scaleM, p));

/** Build an `IntermediateCity` (block coords) from the vector tiles (ERT-2/4). A
 *  feature that straddles tiles appears whole in each intersecting tile, so we dedup
 *  by content key before projecting (the rasterizer is idempotent, but dedup keeps
 *  Poisson tree density and building counts right). */
export function earthCityFromTiles(
  vector: readonly VectorTilePayload[], center: LatLon, scaleM: number, sizeBlocks: number, label: string,
): IntermediateCity {
  const seen = new Set<string>();
  const fresh = <T>(tag: string, v: T): boolean => {
    const k = tag + JSON.stringify(v);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  };
  const roads: IntermediateCity['roads'] = [];
  const water: IntermediateCity['water'] = [];
  const green: IntermediateCity['green'] = [];
  const buildings: IntermediateCity['buildings'] = [];
  const aeroways: IntermediateCity['aeroways'] = [];
  const trees: [number, number][] = [];
  for (const t of vector) {
    for (const r of t.roads) if (fresh('r', r)) roads.push({ points: projectRing(center, scaleM, r.points), roadClass: r.class });
    for (const w of t.water) if (fresh('w', w)) water.push({ points: projectRing(center, scaleM, w.points), holes: w.holes?.map((h) => projectRing(center, scaleM, h)) });
    for (const g of t.green) if (fresh('g', g)) green.push({ points: projectRing(center, scaleM, g.points), holes: g.holes?.map((h) => projectRing(center, scaleM, h)) });
    for (const b of t.buildings) if (fresh('b', b)) buildings.push({ footprint: projectRing(center, scaleM, b.footprint), levels: b.levels, holes: b.holes?.map((h) => projectRing(center, scaleM, h)) });
    for (const a of t.aeroway) if (fresh('a', a)) aeroways.push({ kind: a.kind, points: projectRing(center, scaleM, a.points), widthM: a.widthM, ref: a.ref });
    for (const p of t.trees) if (fresh('t', p)) trees.push(project(center, scaleM, p));
  }
  return { version: 1, scale: scaleM, sizeBlocks, simulated: false, label, roads, water, green, buildings, trees, aeroways };
}

/** A decoded DEM payload → the elevation.ts `DemTile` (lat/lon raster, NW origin).
 *  The payload's own `noData` sentinel is mapped to elevation.ts's `NO_DATA` so the
 *  resample fills gaps rather than treating them as real depths (ETE-3 → E-26). */
export function demTileFromPayload(p: DemTilePayload): DemTile {
  const grid = decodeDemData(p.data);
  const elev = new Array<number>(p.w * p.h);
  for (let i = 0; i < elev.length; i++) {
    const v = grid[i]!;
    elev[i] = v === p.noData || !Number.isFinite(v) ? NO_DATA : v;
  }
  const north = p.origin.lat, west = p.origin.lon;
  const south = north - p.cellDeg.dLat * (p.h - 1);
  const east = west + p.cellDeg.dLon * (p.w - 1);
  return { width: p.w, height: p.h, south, west, north, east, elev, source: p.source };
}

/** Max samples per side of a mosaicked DEM grid (multi-tile worlds); the single-tile
 *  default is returned verbatim (exact), so this only bounds John's real-fetch path. */
const MOSAIC_MAX = 2048;

/** Nearest-cell elevation of the first source tile that contains (lat,lon); NO_DATA
 *  if none does (a mosaic gap, filled later by the resample). */
function sampleTiles(tiles: readonly DemTile[], lat: number, lon: number): number {
  for (const t of tiles) {
    if (lat > t.north || lat < t.south || lon < t.west || lon > t.east) continue;
    const c = Math.round(((lon - t.west) / (t.east - t.west)) * (t.width - 1));
    const r = Math.round(((t.north - lat) / (t.north - t.south)) * (t.height - 1));
    return t.elev[r * t.width + c] ?? NO_DATA;
  }
  return NO_DATA;
}

/** Merge DEM payloads into one `DemTile` (ERT-2). One tile → verbatim (the default,
 *  exact — the ±1-block acceptance path). Many tiles → a union grid nearest-sampled
 *  from the sources at their finest cell size, capped to `MOSAIC_MAX` per side. */
export function earthDemFromTiles(dem: readonly DemTilePayload[]): DemTile | null {
  if (dem.length === 0) return null;
  const tiles = dem.map(demTileFromPayload);
  if (tiles.length === 1) return tiles[0]!;
  let south = Infinity, west = Infinity, north = -Infinity, east = -Infinity, dLat = Infinity, dLon = Infinity;
  for (const t of tiles) {
    south = Math.min(south, t.south); west = Math.min(west, t.west);
    north = Math.max(north, t.north); east = Math.max(east, t.east);
    dLat = Math.min(dLat, (t.north - t.south) / Math.max(1, t.height - 1));
    dLon = Math.min(dLon, (t.east - t.west) / Math.max(1, t.width - 1));
  }
  let width = Math.max(2, Math.round((east - west) / dLon) + 1);
  let height = Math.max(2, Math.round((north - south) / dLat) + 1);
  if (width > MOSAIC_MAX) width = MOSAIC_MAX;
  if (height > MOSAIC_MAX) height = MOSAIC_MAX;
  const elev = new Array<number>(width * height);
  for (let r = 0; r < height; r++) {
    const lat = north - (r / (height - 1)) * (north - south);
    for (let c = 0; c < width; c++) {
      const lon = west + (c / (width - 1)) * (east - west);
      elev[r * width + c] = sampleTiles(tiles, lat, lon);
    }
  }
  return { width, height, south, west, north, east, elev, source: tiles[0]!.source };
}
